import React, { useState, useEffect, useCallback } from 'react';
import './CalendarPage.css';

const API_BASE = '';

function CalendarPage({ isAdmin, authToken }) {
  const [events, setEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '',
    end_time: '',
    location: '',
    description: '',
    photos: []
  });

  const loadEvents = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events`);
      const data = await response.json();
      // Filter to only show community events (not agendas)
      const communityEvents = data.filter(e => e.type !== 'agenda');
      setEvents(communityEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);


  const handleAddEvent = async () => {
    try {
      // Backend expects form data, not JSON
      const formData = new FormData();
      formData.append('type', 'other');
      formData.append('title', newEvent.title);
      formData.append('date', newEvent.date);
      formData.append('time', newEvent.time || '');
      formData.append('end_time', newEvent.end_time || '');
      formData.append('location', newEvent.location || '');
      formData.append('description', newEvent.description || '');
      
      // Add photos if any
      if (newEvent.photos && newEvent.photos.length > 0) {
        newEvent.photos.forEach(photo => {
          formData.append('photos', photo);
        });
      }
      
      const response = await fetch(`${API_BASE}/api/admin/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        setShowEventForm(false);
        setNewEvent({
          title: '',
          date: '',
          time: '',
          end_time: '',
          location: '',
          description: '',
          photos: []
        });
        loadEvents();
      } else {
        alert('Failed to add event');
      }
    } catch (error) {
      console.error('Failed to add event:', error);
      alert('Error adding event');
    }
  };

  const handleUpdateEvent = async () => {
    try {
      // Backend expects form data, not JSON
      const formData = new FormData();
      formData.append('type', editingEvent.type || 'other');
      formData.append('title', editingEvent.title || '');
      formData.append('date', editingEvent.date);
      formData.append('time', editingEvent.time || '');
      formData.append('end_time', editingEvent.end_time || '');
      formData.append('location', editingEvent.location || '');
      formData.append('description', editingEvent.description || '');
      
      const response = await fetch(`${API_BASE}/api/admin/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        setEditingEvent(null);
        loadEvents();
      } else {
        alert('Failed to update event');
      }
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Error updating event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      });

      if (response.ok) {
        loadEvents();
      } else {
        alert('Failed to delete event');
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Error deleting event');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeStr, endTimeStr) => {
    if (!timeStr) return '';
    let result = timeStr;
    if (endTimeStr) {
      result += ` - ${endTimeStr}`;
    }
    return result;
  };

  const sortedEvents = [...events].sort((a, b) => {
    const dateA = new Date(a.date + 'T' + (a.time || '00:00'));
    const dateB = new Date(b.date + 'T' + (b.time || '00:00'));
    return dateA - dateB;
  });

  const upcomingEvents = sortedEvents.filter(event => {
    const eventDate = new Date(event.date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });

  const pastEvents = sortedEvents.filter(event => {
    const eventDate = new Date(event.date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  }).reverse();

  return (
    <div className="calendar-page">
      <div className="page-header">
        <h1>Upcoming Events</h1>
        {isAdmin && (
          <button 
            className="btn btn-primary"
            onClick={() => setShowEventForm(!showEventForm)}
          >
            {showEventForm ? 'Cancel' : '+ Add Event'}
          </button>
        )}
      </div>

      {isAdmin && showEventForm && (
        <div className="event-form">
          <h3>Add New Event</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Event Title</label>
              <input
                type="text"
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                placeholder="Enter event title"
              />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Start Time</label>
              <input
                type="time"
                value={newEvent.time}
                onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>End Time (optional)</label>
              <input
                type="time"
                value={newEvent.end_time}
                onChange={(e) => setNewEvent({...newEvent, end_time: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={newEvent.location}
                onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                placeholder="Enter location"
              />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                placeholder="Enter event description"
                rows={3}
              />
            </div>
            <div className="form-group full-width">
              <label>Event Photos (optional)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setNewEvent({...newEvent, photos: Array.from(e.target.files)})}
                style={{padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
              />
              {newEvent.photos && newEvent.photos.length > 0 && (
                <small style={{color: '#666', marginTop: '5px'}}>
                  {newEvent.photos.length} photo(s) selected
                </small>
              )}
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-success" onClick={handleAddEvent}>
              Add Event
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowEventForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="list-view">
        <div className="events-section">
            <h2>Upcoming Events</h2>
            {upcomingEvents.length === 0 ? (
              <p className="no-events">No upcoming events</p>
            ) : (
              <div className="events-timeline">
                {upcomingEvents.map(event => (
                  <div 
                    key={event.id} 
                    className="timeline-event"
                    onClick={() => setViewingEvent(event)}
                    style={{cursor: 'pointer'}}
                  >
                    <div className="timeline-date">
                      <div className="date-month">
                        {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className="date-day">
                        {new Date(event.date + 'T00:00:00').getDate()}
                      </div>
                    </div>
                    <div className="timeline-content">
                      <h3>{event.title}</h3>
                      {event.time && (
                        <p className="event-time">
                          🕐 {formatTime(event.time, event.end_time)}
                        </p>
                      )}
                      {event.location && (
                        <p className="event-location">📍 {event.location}</p>
                      )}
                      {event.description && (
                        <p className="event-description">{event.description}</p>
                      )}
                      {event.photo_urls && event.photo_urls.length > 0 && (
                        <p className="event-photos-hint">📷 {event.photo_urls.length} photo(s)</p>
                      )}
                      {isAdmin && (
                        <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => setEditingEvent(event)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {pastEvents.length > 0 && (
            <div className="events-section">
              <h2>Past Events</h2>
              <div className="events-timeline past">
                {pastEvents.slice(0, 10).map(event => (
                  <div 
                    key={event.id} 
                    className="timeline-event past-event"
                    onClick={() => setViewingEvent(event)}
                    style={{cursor: 'pointer'}}>
                    <div className="timeline-date">
                      <div className="date-month">
                        {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className="date-day">
                        {new Date(event.date + 'T00:00:00').getDate()}
                      </div>
                    </div>
                    <div className="timeline-content">
                      <h3>{event.title}</h3>
                      {event.description && (
                        <p className="event-description">{event.description}</p>
                      )}
                      {event.photo_urls && event.photo_urls.length > 0 && (
                        <p className="event-photos-hint">📷 {event.photo_urls.length} photo(s)</p>
                      )}
                      {isAdmin && (
                        <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => setEditingEvent(event)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      {editingEvent && (
        <div className="modal-overlay" onClick={() => setEditingEvent(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Event</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Event Title</label>
                <input
                  type="text"
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={editingEvent.date}
                  onChange={(e) => setEditingEvent({...editingEvent, date: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="time"
                  value={editingEvent.time || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, time: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="time"
                  value={editingEvent.end_time || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, end_time: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={editingEvent.location || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, location: e.target.value})}
                />
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={editingEvent.description || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-success" onClick={handleUpdateEvent}>
                Save Changes
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setEditingEvent(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Event Modal */}
      {viewingEvent && (
        <div className="modal-overlay" onClick={() => setViewingEvent(null)}>
          <div className="modal-content event-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{viewingEvent.title}</h2>
              <button className="modal-close" onClick={() => setViewingEvent(null)}>×</button>
            </div>
            <div className="modal-body">
              {viewingEvent.date && (
                <div className="event-detail">
                  <strong>📅 Date:</strong> {formatDate(viewingEvent.date)}
                </div>
              )}
              {viewingEvent.time && (
                <div className="event-detail">
                  <strong>🕐 Time:</strong> {formatTime(viewingEvent.time, viewingEvent.end_time)}
                </div>
              )}
              {viewingEvent.location && (
                <div className="event-detail">
                  <strong>📍 Location:</strong> {viewingEvent.location}
                </div>
              )}
              {viewingEvent.description && (
                <div className="event-detail">
                  <strong>📝 Description:</strong>
                  <p>{viewingEvent.description}</p>
                </div>
              )}
              
              {/* Photo Gallery */}
              {viewingEvent.photo_urls && viewingEvent.photo_urls.length > 0 && (
                <div className="event-photos">
                  <strong>📷 Photos:</strong>
                  <div className="photo-gallery">
                    {viewingEvent.photo_urls.map((url, idx) => (
                      <img 
                        key={idx} 
                        src={url} 
                        alt={`Event photo ${idx + 1}`}
                        onClick={() => window.open(url, '_blank')}
                        style={{cursor: 'pointer'}}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            {isAdmin && (
              <div className="modal-footer">
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingEvent(viewingEvent);
                    setViewingEvent(null);
                  }}
                >
                  Edit Event
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => {
                    handleDeleteEvent(viewingEvent.id);
                    setViewingEvent(null);
                  }}
                >
                  Delete Event
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;